import { useCallback, useState } from 'react';

import type { TimelineActiveItem } from '@hotjar/webapp/features/Playbacks/types';
import { ItemType } from '@hotjar/webapp/features/Playbacks/types';

type Item = {
  id: number | null;
};

interface LollipopInteractionHook {
  deactivateCurrentItem: () => void;
  deactivateNotInteractedCurrentItem: () => void;
  markCurrentItemAsInteracted: () => void;
  makeItemActive: (item: Item) => void;
  openItemPreview: (item: Item) => void;
}

interface LollipopInteractionProps {
  newItemTime: number | null;
  setActiveItem: (item: TimelineActiveItem | null) => void;
}

export function useLollipopInteraction({
  newItemTime,
  setActiveItem,
}: LollipopInteractionProps): LollipopInteractionHook {
  const [interactionWasMade, setInteractionWasMade] = useState(false);

  const deactivateCurrentItem = useCallback(() => {
    setActiveItem(null);
    setInteractionWasMade(false);
  }, [setActiveItem, setInteractionWasMade]);

  const deactivateNotInteractedCurrentItem = useCallback(() => {
    if (interactionWasMade) {
      return;
    }

    deactivateCurrentItem();
  }, [interactionWasMade, deactivateCurrentItem]);

  const markCurrentItemAsInteracted = useCallback(() => {
    if (interactionWasMade) {
      return;
    }

    setInteractionWasMade(true);
  }, [setInteractionWasMade, interactionWasMade]);

  const makeItemActive = useCallback(
    (item) => {
      setActiveItem({
        type: ItemType.HIGHLIGHT,
        id: item.id,
      });
      setInteractionWasMade(true);
    },
    [setActiveItem, setInteractionWasMade],
  );

  const openItemPreview = useCallback(
    (item) => {
      if (interactionWasMade || newItemTime !== null) {
        return;
      }

      setActiveItem({
        type: ItemType.HIGHLIGHT,
        id: item.id,
      });
    },
    [setActiveItem, newItemTime, interactionWasMade],
  );

  return {
    deactivateCurrentItem,
    deactivateNotInteractedCurrentItem,
    markCurrentItemAsInteracted,
    makeItemActive,
    openItemPreview,
  };
}
